import { Update } from "vite";

import { queryClient } from "~/lib/query-client";

import {
  AuditionPollingStatus,
  getProjectDetailsQuery,
  listProjectsQuery,
  VideoDetailsPollingStatus,
} from "./queries";
import {
  AuditionFromAudioVideoItemType,
  ListProjectVideoItemType,
  ProjectDetailsType,
  ProjectListItemType,
} from "./schemas";

export function getActiveVideoResult<
  V extends ListProjectVideoItemType | AuditionFromAudioVideoItemType,
  CR extends VideoDetailsPollingStatus | AuditionPollingStatus,
>(video: V, currentResult: CR, pollingError: Error | null) {
  // If the video is in a terminal state, return the video itself and ignore polling result
  // (which should be none anyway)
  if (isVideoInTerminalState(video)) {
    return video;
  }

  if (currentResult?.state === "failed") {
    return currentResult.result as V;
  } else if (pollingError) {
    return { state: "FAILED", id: video.id } as V;
  }

  return (currentResult?.result ?? video) as V;
}

export function isVideoInTerminalState(
  video: ListProjectVideoItemType | AuditionFromAudioVideoItemType
) {
  return ["COMPLETE", "FAILED", "CANCELLED"].includes(video.state);
}

type UpdatableProjectFields = Pick<
  ProjectDetailsType | ProjectListItemType,
  "title" | "videoCount" | "completedActions"
>;

export function updateProjectDetailsInCache(
  projectId: string,
  updater:
    | ((previousFields: UpdatableProjectFields) => Partial<UpdatableProjectFields>)
    | Partial<UpdatableProjectFields>
) {
  getProjectDetailsQuery.setQueryData(queryClient, { params: { id: projectId } }, (old) => {
    if (!old) {
      return old;
    }

    const updates =
      typeof updater === "function"
        ? updater({
            title: old.project.title,
            videoCount: old.project.videoCount,
            completedActions: old.project.completedActions,
          })
        : updater;

    return {
      ...old,
      project: {
        ...old.project,
        ...updates,
      },
    };
  });

  listProjectsQuery.updateItemsInQueryData(queryClient, { params: {} }, "projects", (project) => {
    if (project.id === projectId) {
      const updates =
        typeof updater === "function"
          ? updater({
              title: project.title,
              videoCount: project.videoCount,
              completedActions: project.completedActions,
            })
          : updater;

      return {
        ...project,
        ...updates,
      };
    }

    return project;
  });
}
